import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    FormLabel,
    Grid,
    IconButton,
    Option,
    Radio,
    RadioGroup,
    SelectField,
    Switch,
    TextField,
    Typography,
} from '@deere/fuel-react';
import { ReactComponent as AddCircleIcon } from '@deere/ux.brand-foundations/icons/add_circle.svg';
import { ReactComponent as CloseIcon } from '@deere/ux.brand-foundations/icons/close.svg';
import { ReactComponent as EditIcon } from '@deere/ux.brand-foundations/icons/edit.svg';
import { DateTime } from 'luxon';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertBox } from '../../components/AlertBox';
import { BidCycleCalendar } from '../../components/BidCycleCalendar';
import { BidCycleSettingsForm } from '../../components/BidCycleSettingsForm';
import { DateInput } from '../../components/DateInput';
import { PromptValidState } from '../../components/navigation/PromptValidState';
import { AdminContext } from '../../context/AdminContext';
import { LoggedInContext } from '../../context/LoggedInContext';
import { errorBannerFromResponse } from '../../utils/errorBanner';
import styles from './Settings.module.css';

const JobBidCycleLengthDisplay = ({
    biddingGroup,
    bidsInProcessData,
    onSaveBidCycleCallback,
}) => {
    const [openModal, setOpenModal] = useState(false);
    const [isBidCycleEditable, setIsBidCycleEditable] = useState(false);
    const [alertBanner, setAlertBanner] = useState(false);
    const WEEKDAYS_FULL = [
        'Sunday',
        'Monday',
        'Tuesday',
        'Wednesday',
        'Thursday',
        'Friday',
        'Saturday',
    ];

    const updateBidCycle = (bidCycle) => {
        onSaveBidCycleCallback(bidCycle);
    };

    const handleClose = () => {
        setOpenModal(false);
    };

    useEffect(() => {
        const getJobCountsByBiddingGroupId = () => {
            const bidGroup = bidsInProcessData.find(
                (group) => group.biddingGroupId === biddingGroup.id
            );
            if (bidGroup) {
                const { active, withdrawn } = bidGroup.jobCounts;
                if (active > 0 || withdrawn > 0) {
                    setAlertBanner({
                        variant: 'error',
                        message:
                            'Bid cycle not editable due to in process bids.',
                    });
                    setIsBidCycleEditable(false);
                    return;
                } else {
                    setIsBidCycleEditable(true);
                    setAlertBanner(false);
                }
            }
            setIsBidCycleEditable(true);
            setAlertBanner(false);
        };
        getJobCountsByBiddingGroupId();
    }, [biddingGroup.id, bidsInProcessData]);

    return (
        <Grid item sm={12}>
            {!biddingGroup.hasCycleSettings && (
                <div className={styles.setBidCycle}>
                    {`${biddingGroup.displayName} Job Bids`}
                    <Button
                        variant="secondary"
                        startIcon={<EditIcon />}
                        name={`Set Job Bid Cycle for ${biddingGroup.displayName}`}
                        aria-label={`Set Job Bid Cycle for ${biddingGroup.displayName}`}
                        disabled={!isBidCycleEditable}
                        onClick={() => setOpenModal(true)}
                    >
                        Set Job Bid Cycle
                    </Button>
                    <BidCycleSettingsForm
                        biddingGroup={biddingGroup}
                        updateBidCycle={updateBidCycle}
                        modalIsOpen={openModal}
                        onCloseModal={handleClose}
                    />
                </div>
            )}
            {biddingGroup.hasCycleSettings && (
                <div className={styles.displayBidCycle}>
                    <span>{`${biddingGroup.displayName} Job Bids`}</span>
                    <Box>
                        {alertBanner && (
                            <AlertBox
                                msg={alertBanner.message}
                                variant={alertBanner.variant}
                            />
                        )}
                    </Box>
                    <div className={styles.bidCycleSettings}>
                        <div>
                            <BidCycleCalendar
                                dayToGoActive={
                                    WEEKDAYS_FULL[biddingGroup.dayToGoActive]
                                }
                                daysInActivePeriod={
                                    biddingGroup.daysInActivePeriod
                                }
                                daysInWithdrawalPeriod={
                                    biddingGroup.daysInWithdrawalPeriod
                                }
                                interval={biddingGroup.bidCycleInterval}
                            />
                        </div>
                        <dl className={styles.bidCycleInfo}>
                            <div>
                                <dt>Job Bids Go Active On: </dt>
                                <dd>
                                    {WEEKDAYS_FULL[biddingGroup.dayToGoActive]}s
                                </dd>
                            </div>
                            <div>
                                <dt>Job Bids Are Active For:</dt>
                                <dd>{biddingGroup.daysInActivePeriod} days</dd>
                            </div>
                            <div>
                                <dt>Bid Withdrawal Period:</dt>
                                <dd>
                                    {biddingGroup.daysInWithdrawalPeriod} days
                                </dd>
                            </div>
                            <div>
                                <dt>Bid Active Time:</dt>
                                <dd>
                                    {DateTime.fromISO(
                                        biddingGroup.timeToGoActive
                                    ).toFormat('t')}
                                </dd>
                            </div>
                            <div>
                                <dt>Time of Bid Close:</dt>
                                <dd>
                                    {DateTime.fromISO(
                                        biddingGroup.timeToClose
                                    ).toFormat('t')}
                                </dd>
                            </div>
                        </dl>
                    </div>
                    <span className={styles.bidCycleKey}>
                        <span className={styles.bidCycleKeyActiveIcon} />
                        <span>Active</span>
                        <span className={styles.bidCycleKeyWithdrawalIcon} />
                        <span>Bid Withdrawal</span>
                    </span>

                    <Button
                        variant="secondary"
                        startIcon={<EditIcon />}
                        name={`Set Job Bid Cycle for ${biddingGroup.displayName}`}
                        aria-label={`Set Job Bid Cycle for ${biddingGroup.displayName}`}
                        disabled={!isBidCycleEditable}
                        onClick={() => setOpenModal(true)}
                    >
                        Set Job Bid Cycle
                    </Button>
                    <BidCycleSettingsForm
                        biddingGroup={biddingGroup}
                        updateBidCycle={updateBidCycle}
                        modalIsOpen={openModal}
                        onCloseModal={handleClose}
                    />
                </div>
            )}
        </Grid>
    );
};

export const Settings = () => {
    const MAX_NUMBER_OF_BUSINESS_UNITS = 12;
    const { selectedAdminGroup, loading, error, updateAdminGroup } =
        useContext(AdminContext);
    const navigate = useNavigate();
    const [isSaving, setIsSaving] = useState(false);
    const [isFormDirty, setIsFormDirty] = useState(false);
    const [adminGroup, setAdminGroup] = useState();
    const [businessUnits, setBusinessUnits] = useState(
        new Array(MAX_NUMBER_OF_BUSINESS_UNITS).fill({ displayName: '' })
    );
    const [isBusinessUnitsInvalid, setIsBusinessUnitsInvalid] = useState(false);
    const [editingEmailKeys, setEditingEmailKeys] = useState([]);
    const [bidsInProcessData, setBidsInProcessData] = useState([]);
    const [alertBanner, setAlertBanner] = useState(null);
    const { makeRequest } = useContext(LoggedInContext);
    const movesInAYearOptions = [
        { value: '-1', label: 'Unlimited' },
        { value: '0', label: '0' },
        { value: '1', label: '1' },
        { value: '2', label: '2' },
        { value: '3', label: '3' },
        { value: '4', label: '4' },
        { value: '5', label: '5' },
        { value: '6', label: '6' },
        { value: '7', label: '7' },
        { value: '8', label: '8' },
        { value: '9', label: '9' },
        { value: '10', label: '10' },
    ];
    // prettier-ignore
    const tiebreakerOptions = [
        { value: 'BY_SSN_ASC', label: 'By SSN - ascending order' },
        { value: 'BY_SSN_DESC', label: 'By SSN - descending order' },
        { value: 'BY_HIRE_NAME_ASC', label: 'By Last Name At Hire - ascending order' },
        { value: 'BY_HIRE_NAME_DESC', label: 'By Last Name At Hire - descending order' },
    ];

    useEffect(() => {
        if (!loading && error) {
            setAlertBanner(
                errorBannerFromResponse(
                    error,
                    'We are unable to retrieve unit settings. Please try again later.'
                )
            );
        }
        if (!loading && !error) {
            setAdminGroup(selectedAdminGroup);
            const currentBusinessUnits =
                selectedAdminGroup.facilityOrBusinessUnits
                    .concat(
                        new Array(
                            MAX_NUMBER_OF_BUSINESS_UNITS -
                                selectedAdminGroup.facilityOrBusinessUnits
                                    .length
                        ).fill({ displayName: '' })
                    )
                    .map((bu, index) => ({ ...bu, index }));
            setBusinessUnits(currentBusinessUnits);

            const checkInProcessBids = async () => {
                const jobCountsResp = await makeRequest(
                    `${process.env.REACT_APP_API_ENDPOINT}/settings/inprocess-bid-counts`
                );
                if (jobCountsResp.ok) {
                    const responseData = await jobCountsResp.json();
                    setBidsInProcessData(responseData);
                } else {
                    setAlertBanner(
                        errorBannerFromResponse(
                            jobCountsResp,
                            'We are unable to check whether there are bids in process at this time.'
                        )
                    );
                }
            };
            checkInProcessBids();
        }
    }, [loading, selectedAdminGroup, error, makeRequest]);

    const hasCycleSettings = (biddingGroup) => {
        return (
            biddingGroup.daysInWithdrawalPeriod !== null &&
            biddingGroup.daysInActivePeriod !== null &&
            biddingGroup.timeToGoActive !== null &&
            biddingGroup.dayToGoActive !== null &&
            biddingGroup.timeToClose !== null
        );
    };

    const handleResponseUpdate = (response) => {
        const updatedFacilityOrBusinessUnits = response.facilityOrBusinessUnits
            .concat(
                new Array(
                    MAX_NUMBER_OF_BUSINESS_UNITS -
                        response.facilityOrBusinessUnits.length
                ).fill({ displayName: '' })
            )
            .map((bu, index) => ({
                ...bu,
                index,
            }));
        const updatedBiddingGroup = response.biddingGroups.map((bg) => {
            return {
                ...bg,
                hasCycleSettings: hasCycleSettings(bg),
            };
        });
        const changedAdminGroup = {
            ...adminGroup,
            biddingGroups: updatedBiddingGroup,
            facilityOrBusinessUnits: updatedFacilityOrBusinessUnits,
        };
        setAdminGroup(changedAdminGroup);
        updateAdminGroup(changedAdminGroup); // Will trigger useEffect by calling selectedAdminGroup
    };

    const handleUpdateBiddingGroups = (updatedBiddingGroups) => {
        const changedAdminGroup = {
            ...adminGroup,
            biddingGroups: updatedBiddingGroups.map((bg) => {
                return {
                    ...bg,
                    hasCycleSettings: hasCycleSettings(bg),
                };
            }),
        };
        setAdminGroup(changedAdminGroup);
        updateAdminGroup(changedAdminGroup);
    };

    const handleUpdateBiddingGroup = (updatedBiddingGroup) => {
        const changedAdminGroup = {
            ...adminGroup,
            biddingGroups: adminGroup.biddingGroups.map((bg) => {
                if (bg.id === updatedBiddingGroup.id) {
                    return {
                        ...updatedBiddingGroup,
                        hasCycleSettings: hasCycleSettings(updatedBiddingGroup),
                    };
                } else {
                    return {
                        ...bg,
                        hasCycleSettings: hasCycleSettings(bg),
                    };
                }
            }),
        };
        setAdminGroup(changedAdminGroup);
        updateAdminGroup(changedAdminGroup);
    };

    const isContractStartInputInvalid = (startDate) => {
        return (
            startDate === null || startDate === '' || startDate === undefined
        );
    };

    const contractStartHandler = (updatedStart, biddingGroup) => {
        if (updatedStart === undefined) {
            return;
        }
        const changedAdminGroup = {
            ...biddingGroup,
            contractStartDate: updatedStart.toISOString(),
            isContractStartDateInvalid:
                isContractStartInputInvalid(updatedStart),
        };
        handleUpdateBiddingGroup(changedAdminGroup);
        setIsFormDirty(true);
    };

    const isWorkforceMovesInputInvalid = (workforceMoves) => {
        return workforceMoves === null || workforceMoves === '';
    };

    const workforceMovesAvailableHandler = (updatedMoves, biddingGroup) => {
        const changedAdminGroup = {
            ...biddingGroup,
            workforceMovesAvailable: updatedMoves,
            isWorkforceMovesAvailableInvalid:
                isWorkforceMovesInputInvalid(updatedMoves),
        };
        handleUpdateBiddingGroup(changedAdminGroup);
        setIsFormDirty(true);
    };

    const isTieBreakerInputInvalid = (tieBreaker) => {
        return tieBreaker === null || tieBreaker === '';
    };

    const tieBreakerHandler = (updateTieBreaker, biddingGroup) => {
        const changedAdminGroup = {
            ...biddingGroup,
            tiebreakerCriteria: updateTieBreaker,
            isTieBreakerInvalid: isTieBreakerInputInvalid(updateTieBreaker),
        };
        handleUpdateBiddingGroup(changedAdminGroup);
        setIsFormDirty(true);
    };

    const notifySuccessfulBiddersHandler = (
        updatedNotifySuccessfulBidders,
        biddingGroup
    ) => {
        const changedAdminGroup = {
            ...biddingGroup,
            notifySuccessfulBidders: updatedNotifySuccessfulBidders,
        };
        handleUpdateBiddingGroup(changedAdminGroup);
        setIsFormDirty(true);
    };

    const JOHN_DEERE_EMAIL = /^[A-Za-z0-9._%+-]+@johndeere\.com$/i;

    const updateNotificationEmails = (biddingGroup, updatedEmails) => {
        handleUpdateBiddingGroup({
            ...biddingGroup,
            notificationEmails: updatedEmails,
        });
        setIsFormDirty(true);
    };

    const addNotificationEmailHandler = (biddingGroup) => {
        const emails = biddingGroup.notificationEmails ?? [];
        setEditingEmailKeys((keys) => [
            ...keys,
            `${biddingGroup.id}-${emails.length}`,
        ]);
        updateNotificationEmails(biddingGroup, [...emails, '']);
    };

    const editNotificationEmailHandler = (biddingGroup, index) => {
        setEditingEmailKeys((keys) => [...keys, `${biddingGroup.id}-${index}`]);
    };

    const changeNotificationEmailHandler = (biddingGroup, index, value) => {
        const emails = [...(biddingGroup.notificationEmails ?? [])];
        emails[index] = value;
        updateNotificationEmails(biddingGroup, emails);
    };

    const deleteNotificationEmailHandler = (biddingGroup, index) => {
        const emails = (biddingGroup.notificationEmails ?? []).filter(
            (email, emailIndex) => emailIndex !== index
        );
        setEditingEmailKeys((keys) =>
            keys.filter((key) => !key.startsWith(`${biddingGroup.id}-`))
        );
        updateNotificationEmails(biddingGroup, emails);
    };

    const businessUnitHandler = (updatedName, businessUnit) => {
        const updatedBusinessUnits = businessUnits.map((bu) => {
            if (bu.index === businessUnit.index) {
                return {
                    ...businessUnit,
                    displayName: updatedName,
                };
            } else {
                return bu;
            }
        });
        setBusinessUnits(updatedBusinessUnits);
        setIsFormDirty(true);
        const businessUnitsAreInvalid = updatedBusinessUnits.every(
            (bu) => bu.displayName === ''
        );
        setIsBusinessUnitsInvalid(businessUnitsAreInvalid);
    };

    const isFormInvalid = () => {
        const validatedAdminGroup = {
            ...adminGroup,
            biddingGroups: adminGroup.biddingGroups.map((bg) => {
                const enteredEmails = (bg.notificationEmails ?? [])
                    .map((email) => email.trim())
                    .filter((email) => email !== '');
                return {
                    ...bg,
                    isWorkforceMovesAvailableInvalid:
                        isWorkforceMovesInputInvalid(
                            bg.workforceMovesAvailable
                        ),
                    isTieBreakerInvalid: isTieBreakerInputInvalid(
                        bg.tiebreakerCriteria
                    ),
                    isContractStartDateInvalid: isContractStartInputInvalid(
                        bg.contractStartDate
                    ),
                    isNotificationEmailsInvalid: enteredEmails.some(
                        (email) => !JOHN_DEERE_EMAIL.test(email)
                    ),
                    isNotificationEmailsMissing:
                        Boolean(bg.notifySuccessfulBidders) &&
                        enteredEmails.filter((email) =>
                            JOHN_DEERE_EMAIL.test(email)
                        ).length === 0,
                };
            }),
        };
        setAdminGroup(validatedAdminGroup);
        const biddingGroupsAreInvalid = validatedAdminGroup.biddingGroups.some(
            (bg) => {
                return (
                    bg.isWorkforceMovesAvailableInvalid ||
                    bg.isTieBreakerInvalid ||
                    bg.isContractStartDateInvalid ||
                    bg.isNotificationEmailsInvalid ||
                    bg.isNotificationEmailsMissing
                );
            }
        );
        const businessUnitsAreInvalid = businessUnits.every(
            (bu) => bu.displayName === ''
        );
        setIsBusinessUnitsInvalid(businessUnitsAreInvalid);

        return biddingGroupsAreInvalid || businessUnitsAreInvalid;
    };

    const saveHandler = async () => {
        setIsSaving(true);
        if (isFormInvalid()) {
            setAlertBanner({
                variant: 'error',
                message:
                    'You must fill out all required fields to create a new job bid.',
            });
            setIsSaving(false);
            window.scrollTo(0, 0);
            return;
        }

        const biddingGroupsToSend = adminGroup.biddingGroups.map((bidGroup) => {
            return {
                id: bidGroup.id,
                displayName: bidGroup.displayName,
                contractStartDate: bidGroup.contractStartDate,
                workforceMovesAvailable: bidGroup.workforceMovesAvailable,
                tiebreakerCriteria: bidGroup.tiebreakerCriteria,
                notifySuccessfulBidders: Boolean(
                    bidGroup.notifySuccessfulBidders
                ),
                notificationEmails: (bidGroup.notificationEmails ?? [])
                    .map((email) => email.trim())
                    .filter((email) => email !== ''),
                bidCycleInterval: bidGroup.bidCycleInterval,
                dayToGoActive: bidGroup.dayToGoActive,
                daysInActivePeriod: bidGroup.daysInActivePeriod,
                daysInWithdrawalPeriod: bidGroup.daysInWithdrawalPeriod,
                hasCycleSettings: bidGroup.hasCycleSettings,
                timeToClose: bidGroup.timeToClose,
                timeToGoActive: bidGroup.timeToGoActive,
            };
        });
        const updatedAdminGroup = {
            ...adminGroup,
            facilityOrBusinessUnits: businessUnits.filter(
                (bu) => bu.displayName !== ''
            ),
            biddingGroups: biddingGroupsToSend,
        };
        const requestOptions = {
            method: 'PUT',
            body: JSON.stringify(updatedAdminGroup),
        };
        const updatedResponse = await makeRequest(
            `${process.env.REACT_APP_API_ENDPOINT}/settings/admin-group/${adminGroup.adGroup}`,
            requestOptions
        );
        if (updatedResponse.ok) {
            handleResponseUpdate(await updatedResponse.json());
            setAlertBanner({
                variant: 'success',
                message: `You have successfully edited settings for ${adminGroup.displayName}`,
            });
            setIsSaving(false);
            setIsFormDirty(false);
            setEditingEmailKeys([]);
        } else {
            setAlertBanner(
                errorBannerFromResponse(
                    updatedResponse,
                    'Your settings were not saved. Please try again later.'
                )
            );
            setIsSaving(false);
        }
        window.scrollTo(0, 0);
    };

    const setJobBidSavedCallback = async (bidCycle) => {
        const updatedBiddingGroups = adminGroup.biddingGroups.map((bg) => {
            if (bg.id === bidCycle.id) {
                return {
                    ...bg,
                    daysInWithdrawalPeriod: bidCycle.daysInWithdrawalPeriod,
                    daysInActivePeriod: bidCycle.daysInActivePeriod,
                    timeToGoActive: bidCycle.timeToGoActive,
                    dayToGoActive: bidCycle.dayToGoActive,
                    timeToClose: bidCycle.timeToClose,
                    bidCycleInterval: bidCycle.bidCycleInterval,
                    hasCycleSettings: true,
                };
            } else {
                return bg;
            }
        });
        handleUpdateBiddingGroups(updatedBiddingGroups);
    };

    return (
        <div className={styles.parentContainer}>
            <Box spacing={6} className={styles.contentContainer}>
                {!loading && alertBanner && (
                    <AlertBox
                        variant={alertBanner.variant}
                        msg={alertBanner.message}
                    />
                )}
                <Box className={styles.headerContainer} display="block">
                    <Typography variant="h1" className={styles.heading}>
                        Settings
                    </Typography>
                    Choose job bidding settings for your unit, which will be
                    applied to all newly created job bids.
                </Box>
                {loading && (
                    <CircularProgress
                        size="lg"
                        variant="indeterminate"
                        title={'Loading'}
                    />
                )}
                {!loading && adminGroup && (
                    <>
                        <PromptValidState
                            isFormChanged={isFormDirty}
                            saveChanges={saveHandler}
                            areAllFieldsValid={() => {
                                return true;
                            }}
                        />
                        <Box className={styles.required}>
                            <span>* </span>
                            Required fields.
                        </Box>

                        {/* Contract/Fiscal Year Start Date */}
                        <Grid
                            container
                            className={styles.verticalGridSection}
                            spacing={4}
                        >
                            <Grid item sm={12}>
                                <Typography
                                    variant="h3"
                                    className={styles.heading}
                                >
                                    Bidding Groups - Personnel Sub Areas
                                </Typography>
                                <span>
                                    Records show that you have the following
                                    personnel sub areas in your unit. Choose the
                                    contract start date for each area in your
                                    unit. You may choose the same day benefits
                                    renew as the start date.{' '}
                                    <strong>
                                        Non-Bargained is for non-union employees
                                    </strong>
                                    .
                                </span>
                            </Grid>
                            {adminGroup.biddingGroups.map(
                                (biddingGroup, index) => {
                                    return (
                                        <Grid item sm={4} key={index}>
                                            <FormLabel
                                                className={styles.formLabel}
                                                required
                                                error={
                                                    biddingGroup.isContractStartDateInvalid
                                                }
                                            >
                                                {biddingGroup.displayName}
                                            </FormLabel>
                                            <DateInput
                                                required
                                                value={DateTime.fromISO(
                                                    biddingGroup.contractStartDate,
                                                    { zone: 'utc' }
                                                ).toFormat('dd-LLL-yyyy')}
                                                setValue={(date) =>
                                                    contractStartHandler(
                                                        date,
                                                        biddingGroup
                                                    )
                                                }
                                                helperText="*Contract/Fiscal Year Start Date"
                                                error={
                                                    biddingGroup.isContractStartDateInvalid
                                                }
                                            />
                                        </Grid>
                                    );
                                }
                            )}
                        </Grid>

                        {/* Moves in a Contract/Fiscal Year */}
                        <Grid
                            container
                            className={styles.verticalGridSection}
                            spacing={4}
                        >
                            <Grid item sm={12}>
                                <Typography
                                    variant="h3"
                                    className={styles.heading}
                                >
                                    Moves in a Contract/Fiscal Year
                                </Typography>
                                <span>
                                    Choose the maximum number of job moves an
                                    employee is allowed to make within a given
                                    contract/fiscal year. Making a move means
                                    the employee was marked as successful for a
                                    job bid.{' '}
                                </span>
                            </Grid>
                            {adminGroup.biddingGroups.map(
                                (biddingGroup, index) => {
                                    return (
                                        <Grid item sm={4} key={index}>
                                            <SelectField
                                                required
                                                label={`${biddingGroup.displayName} Contract Year`}
                                                helperText={
                                                    '*Max Number of Moves'
                                                }
                                                value={
                                                    biddingGroup.workforceMovesAvailable ??
                                                    ''
                                                }
                                                onChange={(event) =>
                                                    workforceMovesAvailableHandler(
                                                        event.target.value,
                                                        biddingGroup
                                                    )
                                                }
                                                error={
                                                    biddingGroup.isWorkforceMovesAvailableInvalid
                                                }
                                            >
                                                <Option
                                                    disabled
                                                    hidden
                                                    value=""
                                                >
                                                    Choose a number of moves
                                                </Option>
                                                {movesInAYearOptions.map(
                                                    (movesOption, optIndex) => {
                                                        return (
                                                            <Option
                                                                value={
                                                                    movesOption.value
                                                                }
                                                                key={optIndex}
                                                            >
                                                                {
                                                                    movesOption.label
                                                                }
                                                            </Option>
                                                        );
                                                    }
                                                )}
                                            </SelectField>
                                        </Grid>
                                    );
                                }
                            )}
                        </Grid>

                        {/* Tie Breakers */}
                        <Grid
                            container
                            className={styles.horizontalGridSection}
                            spacing={4}
                        >
                            <Grid item sm={12}>
                                <Typography
                                    variant="h3"
                                    className={styles.heading}
                                >
                                    Tie Breakers
                                </Typography>
                                <span>
                                    Choose a single tie breaker that your unit
                                    wants to use, for all personnel sub areas,
                                    in the case of a tie.
                                </span>
                            </Grid>
                            {adminGroup.biddingGroups.map(
                                (biddingGroup, index) => {
                                    return (
                                        <Grid item sm={12} key={index}>
                                            <FormLabel
                                                className={styles.formLabel}
                                                error={
                                                    biddingGroup.isTieBreakerInvalid
                                                }
                                                required
                                            >
                                                {biddingGroup.displayName}
                                            </FormLabel>
                                            <RadioGroup
                                                aria-label={`Tie Breaker for ${biddingGroup.displayName}`}
                                                name={`Tie Breaker for ${biddingGroup.displayName}`}
                                                value={
                                                    biddingGroup.tiebreakerCriteria
                                                }
                                                error={
                                                    biddingGroup.isTieBreakerInvalid
                                                        ? 'invalid'
                                                        : null
                                                }
                                                className={styles.setTieBreaker}
                                                onChange={(event) =>
                                                    tieBreakerHandler(
                                                        event.target.value,
                                                        biddingGroup
                                                    )
                                                }
                                            >
                                                {tiebreakerOptions.map(
                                                    (tieOption, i) => {
                                                        return (
                                                            <FormControlLabel
                                                                key={i}
                                                                control={
                                                                    <Radio />
                                                                }
                                                                label={
                                                                    tieOption.label
                                                                }
                                                                value={
                                                                    tieOption.value
                                                                }
                                                            />
                                                        );
                                                    }
                                                )}
                                            </RadioGroup>
                                        </Grid>
                                    );
                                }
                            )}
                        </Grid>

                        {/* Notify Successful Bidders */}
                        <Grid
                            container
                            className={styles.horizontalGridSection}
                            spacing={4}
                        >
                            <Grid item sm={12}>
                                <Typography
                                    variant="h3"
                                    className={styles.heading}
                                >
                                    Notify Successful Bidders
                                </Typography>
                                <span>
                                    When turned on, the system emails each
                                    bidder awarded an opening once the job is
                                    finalized. Every award email is also sent to
                                    that group&apos;s Admin Email Recipients
                                    below, along with alerts about bidders with
                                    no email on file. At least one address is
                                    required while notifications are turned on.
                                </span>
                            </Grid>
                            {adminGroup.biddingGroups.map(
                                (biddingGroup, index) => {
                                    const emails =
                                        biddingGroup.notificationEmails ?? [];
                                    return (
                                        <Grid item sm={12} key={index}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        inputProps={{
                                                            'aria-label': `Notify Successful Bidders for ${biddingGroup.displayName}`,
                                                            name: `Notify Successful Bidders for ${biddingGroup.displayName}`,
                                                        }}
                                                        checked={Boolean(
                                                            biddingGroup.notifySuccessfulBidders
                                                        )}
                                                        onChange={(event) =>
                                                            notifySuccessfulBiddersHandler(
                                                                event.target
                                                                    .checked,
                                                                biddingGroup
                                                            )
                                                        }
                                                    />
                                                }
                                                label={biddingGroup.displayName}
                                            />
                                            <div className={styles.emailBlock}>
                                                <span
                                                    className={styles.important}
                                                >
                                                    Admin Email Recipients
                                                    {Boolean(
                                                        biddingGroup.notifySuccessfulBidders
                                                    ) && <span> *</span>}
                                                </span>
                                                {emails.map(
                                                    (email, emailIndex) => {
                                                        const isEditing =
                                                            editingEmailKeys.includes(
                                                                `${biddingGroup.id}-${emailIndex}`
                                                            );
                                                        return (
                                                            <div
                                                                className={
                                                                    styles.emailRow
                                                                }
                                                                key={emailIndex}
                                                            >
                                                                {isEditing ? (
                                                                    <TextField
                                                                        label="Email Address"
                                                                        placeholder="name@JohnDeere.com"
                                                                        inputProps={{
                                                                            'aria-label': `Email Address ${
                                                                                emailIndex +
                                                                                1
                                                                            } for ${
                                                                                biddingGroup.displayName
                                                                            }`,
                                                                        }}
                                                                        error={
                                                                            email.trim() !==
                                                                                '' &&
                                                                            !JOHN_DEERE_EMAIL.test(
                                                                                email.trim()
                                                                            )
                                                                        }
                                                                        helperText={
                                                                            email.trim() !==
                                                                                '' &&
                                                                            !JOHN_DEERE_EMAIL.test(
                                                                                email.trim()
                                                                            )
                                                                                ? 'Enter a John Deere email address (name@JohnDeere.com).'
                                                                                : undefined
                                                                        }
                                                                        value={
                                                                            email
                                                                        }
                                                                        onChange={(
                                                                            event
                                                                        ) =>
                                                                            changeNotificationEmailHandler(
                                                                                biddingGroup,
                                                                                emailIndex,
                                                                                event
                                                                                    .target
                                                                                    .value
                                                                            )
                                                                        }
                                                                    />
                                                                ) : (
                                                                    <>
                                                                        <span>
                                                                            {
                                                                                email
                                                                            }
                                                                        </span>
                                                                        <IconButton
                                                                            aria-label={`Edit ${email} for ${biddingGroup.displayName}`}
                                                                            onClick={() =>
                                                                                editNotificationEmailHandler(
                                                                                    biddingGroup,
                                                                                    emailIndex
                                                                                )
                                                                            }
                                                                        >
                                                                            <EditIcon />
                                                                        </IconButton>
                                                                    </>
                                                                )}
                                                                <IconButton
                                                                    aria-label={`Delete ${
                                                                        email ===
                                                                        ''
                                                                            ? `Email Address ${
                                                                                  emailIndex +
                                                                                  1
                                                                              }`
                                                                            : email
                                                                    } for ${
                                                                        biddingGroup.displayName
                                                                    }`}
                                                                    onClick={() =>
                                                                        deleteNotificationEmailHandler(
                                                                            biddingGroup,
                                                                            emailIndex
                                                                        )
                                                                    }
                                                                >
                                                                    <CloseIcon />
                                                                </IconButton>
                                                            </div>
                                                        );
                                                    }
                                                )}
                                                {biddingGroup.isNotificationEmailsMissing && (
                                                    <FormLabel
                                                        error
                                                        className={
                                                            styles.formLabel
                                                        }
                                                    >
                                                        At least one John Deere
                                                        email address is
                                                        required while
                                                        notifications are turned
                                                        on.
                                                    </FormLabel>
                                                )}
                                                <div>
                                                    <Button
                                                        variant="secondary"
                                                        aria-label={`Add Email Address for ${biddingGroup.displayName}`}
                                                        startIcon={
                                                            <AddCircleIcon />
                                                        }
                                                        onClick={() =>
                                                            addNotificationEmailHandler(
                                                                biddingGroup
                                                            )
                                                        }
                                                    >
                                                        {`Add Email Address for ${biddingGroup.displayName}`}
                                                    </Button>
                                                </div>
                                            </div>
                                        </Grid>
                                    );
                                }
                            )}
                        </Grid>

                        {/* Job Bid Cycle Length */}
                        <Grid
                            container
                            className={styles.horizontalGridSection}
                            spacing={4}
                        >
                            <Grid item sm={12}>
                                <Typography
                                    variant="h3"
                                    className={styles.heading}
                                >
                                    Job Bid Cycle Length
                                </Typography>
                                <span>
                                    Each personnel sub area within your unit
                                    must have a defined job bid cycle schedule.
                                    Once you make these selections they will be
                                    locked in.{' '}
                                    {adminGroup.biddingGroups.some(
                                        (bg) => !bg.hasCycleSettings
                                    ) && (
                                        <span className={styles.important}>
                                            You will not be able to create jobs
                                            for any personnel sub areas below
                                            that do not have a set job bid cycle
                                            length.
                                        </span>
                                    )}
                                </span>
                            </Grid>
                            <div className={styles.bidCycleList}>
                                {adminGroup.biddingGroups.map(
                                    (biddingGroup, index) => {
                                        return (
                                            <JobBidCycleLengthDisplay
                                                key={index}
                                                biddingGroup={biddingGroup}
                                                bidsInProcessData={
                                                    bidsInProcessData
                                                }
                                                onSaveBidCycleCallback={
                                                    setJobBidSavedCallback
                                                }
                                            />
                                        );
                                    }
                                )}
                            </div>
                        </Grid>

                        {/* Facilities or Business Units */}
                        <Grid
                            container
                            className={styles.verticalGridSection}
                            spacing={4}
                        >
                            <Grid item sm={12}>
                                <Typography
                                    variant="h3"
                                    className={styles.heading}
                                >
                                    Facilities or Business Units
                                </Typography>
                                Jobs are usually categorized by either facility
                                or business unit. Enter the names of ALL the
                                facilities or business units within your unit
                                that you will create jobs for.{' '}
                                <span className={styles.important}>
                                    Only fill out as many as your unit requires
                                    and leave the rest blank.
                                </span>
                            </Grid>
                            {businessUnits
                                .sort((a, b) => a.id - b.id)
                                .map((bu, index) => {
                                    return (
                                        <Grid item sm={4} key={index}>
                                            <TextField
                                                error={isBusinessUnitsInvalid}
                                                required={index === 0}
                                                size="small"
                                                label="Facility or Business Unit"
                                                placeholder="Facility or Business Unit"
                                                value={bu.displayName}
                                                onChange={(event) =>
                                                    businessUnitHandler(
                                                        event.target.value,
                                                        bu
                                                    )
                                                }
                                            />
                                        </Grid>
                                    );
                                })}
                        </Grid>
                        <Grid item sm={12} className={styles.formActions}>
                            <Button
                                variant="secondary"
                                onClick={() => navigate('/')}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={saveHandler}
                                disabled={isSaving}
                            >
                                {isSaving ? (
                                    <CircularProgress
                                        variant="indeterminate"
                                        title={'Saving'}
                                    />
                                ) : (
                                    'Save'
                                )}
                            </Button>
                        </Grid>
                    </>
                )}
            </Box>
        </div>
    );
};
